import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import "../glow-effect.css";

const BOOK_URL = "/academy-book.php";
const TZ = "Europe/Madrid";
const WA = (msg) =>
  `https://wa.me/34666286672?text=${encodeURIComponent(msg)}`;
const MAIL =
  "mailto:contact@peoplefortech.com?subject=" +
  encodeURIComponent("Reserva clase IA gratis") +
  "&body=" +
  encodeURIComponent(
    "Hola, quiero reservar la primera clase de IA (gratis, 30 min). Mi horario preferido: "
  );

const TIMES = ["15:00", "15:30", "16:00", "16:30", "17:00", "17:30"];

const ymdInMadrid = (d) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);

const weekdayNum = (ymd) => {
  const [y, m, day] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, day, 12, 0, 0)).getUTCDay();
};

const labelDay = (ymd, locale) => {
  const [y, m, day] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(dt);
};

function nextWeekdays(count = 10) {
  const days = [];
  const cursor = new Date();
  for (let i = 0; i < 40 && days.length < count; i += 1) {
    const ymd = ymdInMadrid(cursor);
    const dow = weekdayNum(ymd);
    if (dow >= 1 && dow <= 5) days.push(ymd);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function isPast(ymd, time) {
  const nowYmd = ymdInMadrid(new Date());
  const nowHm = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
  if (ymd < nowYmd) return true;
  if (ymd > nowYmd) return false;
  return time <= nowHm;
}

const AcademyBooking = ({ topic = "" }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "es").split("-")[0];
  const days = useMemo(() => nextWeekdays(10), []);
  const [date, setDate] = useState(days[0] || "");
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [booked, setBooked] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BOOK_URL}?t=${Date.now()}`, { cache: "no-store" });
      const data = await res.json();
      if (data && data.ok && Array.isArray(data.booked)) {
        setBooked(data.booked);
      }
    } catch {
      setBooked([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      setError(t("academy.bookPickSlot"));
      return;
    }
    setStatus("saving");
    setError("");
    try {
      const res = await fetch(BOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          topic,
          date,
          time,
          website,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || t("academy.bookFail"));
      }
      setDone({ date: data.date, time: data.time });
      setStatus("ok");
      setTime("");
      await load();
    } catch (err) {
      setStatus("idle");
      setError(err.message || t("academy.bookFail"));
      await load();
    }
  };

  return (
    <Box
      id="reservar"
      component="section"
      sx={{
        maxWidth: 560,
        mx: "auto",
        px: 2,
        py: 4,
        textAlign: "center",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#111", mb: 0.5 }}>
        {t("academy.bookTitle")}
      </Typography>
      <Typography variant="body2" sx={{ color: "#333", mb: 3 }}>
        {t("academy.bookSubtitle")}
      </Typography>

      {status === "ok" && done ? (
        <Alert severity="success" sx={{ textAlign: "left", mb: 2 }}>
          {t("academy.bookOk", { date: done.date, time: done.time })}
        </Alert>
      ) : (
        <Box component="form" onSubmit={submit}>
          <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 1, mb: 2 }}>
            {days.map((d) => (
              <Button
                key={d}
                type="button"
                variant={date === d ? "contained" : "outlined"}
                onClick={() => {
                  setDate(d);
                  setTime("");
                }}
                sx={{
                  flex: "0 0 auto",
                  textTransform: "none",
                  color: date === d ? "#fff" : "#111",
                  bgcolor: date === d ? "#111" : "#fff",
                  borderColor: "#111",
                  minWidth: 88,
                }}
              >
                {labelDay(d, locale)}
              </Button>
            ))}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
              mb: 2,
            }}
          >
            {TIMES.map((tm) => {
              const id = `${date}T${tm}`;
              const taken = booked.includes(id) || isPast(date, tm);
              const selected = time === tm;
              return (
                <Button
                  key={tm}
                  type="button"
                  disabled={taken}
                  variant={selected ? "contained" : "outlined"}
                  onClick={() => setTime(tm)}
                  sx={{
                    textTransform: "none",
                    color: taken ? "#999" : selected ? "#fff" : "#111",
                    bgcolor: selected ? "#111" : "#fff",
                    borderColor: "#111",
                    textDecoration: taken ? "line-through" : "none",
                  }}
                >
                  {tm}
                </Button>
              );
            })}
          </Box>

          <TextField
            required
            fullWidth
            size="small"
            label={t("academy.bookName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 1.5, bgcolor: "#fff" }}
          />
          <TextField
            required
            fullWidth
            size="small"
            type="email"
            label={t("academy.bookEmail")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 1.5, bgcolor: "#fff" }}
          />
          <Box sx={{ position: "absolute", left: -9999, opacity: 0 }} aria-hidden>
            <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </Box>

          {error ? (
            <Alert severity="error" sx={{ textAlign: "left", mb: 1.5 }}>
              {error}
            </Alert>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            className="glow-on-hover"
            disabled={status === "saving"}
            sx={{
              px: 4,
              minHeight: 50,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#000",
              "&:hover": { bgcolor: "#333" },
            }}
          >
            {status === "saving" ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : t("academy.bookCta")}
          </Button>
        </Box>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 3 }}>
        <MuiLink
          href={WA("Hola, quiero la primera clase de IA gratis")}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontWeight: 600, color: "#1e2a5a" }}
        >
          {t("academy.ctaWhatsapp")}
        </MuiLink>
        <MuiLink href={MAIL} underline="hover" sx={{ fontWeight: 600, color: "#1e2a5a" }}>
          {t("academy.ctaEmail")}
        </MuiLink>
      </Stack>
    </Box>
  );
};

export default AcademyBooking;
